import axios, { AxiosError, AxiosHeaders, AxiosInstance } from 'axios';

import ApiCache from './apiCache';

const formatError = (error: AxiosError) => ({
  code: error.response?.status,
  message: error.message,
  data: error.response?.data,
});

const apiCache = new ApiCache();
const axiosInstances: { [key: string]: AxiosInstance } = {};

/**
 * One axios instance per base URL and token, each with its own interceptors.
 *
 * This used to configure the global `axios` object instead, adding one request interceptor per
 * token to it. Interceptors accumulate on the object they are added to, and axios runs request
 * interceptors in reverse order of registration, so the first token a page ever saw set the
 * Authorization header last and won on every request that followed, until a full reload: after a
 * login, after a logout, and on requests meant to be anonymous. A tab that loaded with a token the
 * server no longer accepted therefore got a 401 from `Guardian.Plug.VerifyHeader` on every call,
 * public ones included. On 01/10/2026 that kept a worker out of an application for half an hour:
 * the job page sent his old, rejected token before and after he logged in, the CV upload and the
 * missing-data form both answered 401, and "Candidati" kept showing the anonymous flow.
 */
export const getAxiosInstance = (base_url: string, token?: string) => {
  const axiosInstance = axios.create({ baseURL: base_url });

  axiosInstance.interceptors.request.use((config) => {
    apiCache.handleRequest(config);
    if (token && config.headers) (config.headers as AxiosHeaders).set('Authorization', `Bearer ${token}`);
    return config;
  });

  axiosInstance.interceptors.response.use(
    (response) => apiCache.handleResponse(response),
    (error: AxiosError) => {
      return Promise.reject(error.response ? formatError({ ...error }) : error);
    }
  );
  return axiosInstance;
};

export const dynamicAxios = (base_url: string, token?: string) => {
  const key = `${base_url}_${token || ''}`;
  const existingAxiosInstance = axiosInstances[key];
  if (!existingAxiosInstance) {
    const newAxiosInstance = getAxiosInstance(base_url, token);
    axiosInstances[key] = newAxiosInstance;
    return newAxiosInstance;
  }
  return existingAxiosInstance;
};

export default dynamicAxios;
export type AxiosInstanceType = AxiosInstance;
